/**
 * Fall overseeding for cool-season lawns: one rule for every surface (the
 * Seeding tool, the Soil temperature tool, the Map, the soil hero, the plan's
 * overseed window and the weekly plan). Decision and citations:
 * obsidian/decisions/2026-09-28-fall-overseed-and-mow-height-rules.md.
 *
 * The call turns on days left before the location's median first fall frost
 * (NOAA 1991-2020, 32°F), with a per-species lead time, and on soil
 * temperature when a reading is at hand:
 *
 *   good      enough time for this species to sprout and establish
 *   late      short on time; seed now, perennial rye covers fastest
 *   too_late  plan dormant seeding (after the hard-freeze normal) or spring
 *   waiting   before the window, or soil still above 65°F
 *
 * Pure: imports only the frost helpers, so the mobile app shares it through
 * `@shared/calculators/fall-overseed`.
 */

import { dateFromDoy, doyOf, type FrostNormals } from "./frost-normals"

export type OverseedSpecies = "perennial_rye" | "tall_fescue" | "fine_fescue" | "kentucky_bluegrass"

export interface OverseedSpeciesRule {
  /** Lowercase name for copy, e.g. "bluegrass". */
  label: string
  /** Typical days to emerge in fall soil. */
  germinationDays: readonly [number, number]
  /** Good when at least this many days remain before the median first frost. */
  goodLeadDays: number
  /** Late but possible down to this many days; too late below it. */
  lateLeadDays: number
}

// Lead times: extension windows (Rutgers FS584, Purdue AY-3, Penn State,
// Cornell, UMass) end 36-56 days before the NOAA median first 32°F date of
// their reference stations; Missouri Extension's floor is "four to six weeks
// before the first killing frost". Slower species need the longer lead.
export const OVERSEED_SPECIES: Readonly<Record<OverseedSpecies, OverseedSpeciesRule>> = {
  perennial_rye: { label: "perennial rye", germinationDays: [5, 10], goodLeadDays: 42, lateLeadDays: 28 },
  tall_fescue: { label: "tall fescue", germinationDays: [7, 14], goodLeadDays: 49, lateLeadDays: 35 },
  fine_fescue: { label: "fine fescue", germinationDays: [7, 14], goodLeadDays: 49, lateLeadDays: 35 },
  kentucky_bluegrass: { label: "bluegrass", germinationDays: [14, 30], goodLeadDays: 56, lateLeadDays: 42 },
}

/** Cool-season seed sprouts and roots best in 50-65°F soil (UMass turf BMPs). */
export const FALL_OVERSEED_SOIL_F = { min: 50, max: 65 } as const

/** Earliest the fall window opens anywhere, and how far ahead of frost it opens. */
const EARLIEST_OPEN = { month: 7, day: 10 } as const
const OPEN_LEAD_DAYS = 85
/** Frost-free locations: seed from Sep 1 through Nov 30 when soil allows. */
const FROST_FREE_WINDOW = { open: { month: 8, day: 1 }, close: { month: 10, day: 30 } } as const
/**
 * Used only when a location has no normals at all: a Nov 1 first frost, the
 * middle of the extension calendars (Aug 15 to mid-Sep seeding).
 */
const FALLBACK_FROST: FrostNormals = { zip3: 0, first32Doy: 305, first28Doy: 316 }
/** Warm soil stops being a reason to wait when fewer days than this remain. */
const SOIL_WAIT_MIN_DAYS = 7
/** Dormant seeding is only offered where the hard freeze comes by mid-December. */
const DORMANT_SEED_LAST_DOY = 345
/**
 * Dormant seeding waits two weeks past the median first 28°F night, so soil
 * has settled below 40-45°F and a warm spell will not sprout the seed
 * (UMass, Cornell, UMN: soil under 40-45°F, typically after Thanksgiving in
 * the Northeast).
 */
const DORMANT_SEED_AFTER_HARD_FREEZE_DAYS = 14

const DAY_MS = 86400000

/** The species a grass name names, or null for mixes and unknown grass. */
function namedSpecies(grassType: string | null | undefined): OverseedSpecies | null {
  const g = (grassType ?? "").toLowerCase()
  if (g.includes("mix") || g.includes("blend")) return null
  if (g.includes("bluegrass") || g === "kbg") return "kentucky_bluegrass"
  if (g.includes("rye")) return "perennial_rye"
  if (g.includes("fine fescue") || g.includes("red fescue") || g.includes("hard fescue") || g.includes("chewings")) {
    return "fine_fescue"
  }
  if (g.includes("fescue")) return "tall_fescue"
  return null
}

/** The seeding rule for a stored or display grass name. Unknown and mixed cool-season lawns use tall fescue. */
export function overseedSpeciesFor(grassType: string | null | undefined): OverseedSpecies {
  return namedSpecies(grassType) ?? "tall_fescue"
}

export type FallOverseedStage = "waiting" | "good" | "late" | "too_late"
export type FallOverseedReason =
  | "soil_warm"
  | "before_window"
  | "calendar"
  | "soil_cool"
  | "frost_free"
  | "no_frost_data"

export interface FallOverseedInput {
  grassType?: string | null
  now: Date
  frost: FrostNormals | null
  /** Current soil temperature, °F, when a reading is at hand. */
  soilF?: number | null
}

export interface FallOverseedCall {
  stage: FallOverseedStage
  reason: FallOverseedReason
  species: OverseedSpecies
  /** Days from `now` to the median first 32°F night. Null without frost data. */
  daysToFrost: number | null
  /** Median first 32°F night this fall. */
  frostDate: Date | null
  /** First day of this fall's window. */
  opensOn: Date | null
  /** Last day the call stays good for this species. */
  goodUntil: Date | null
  /** Last day the call stays late-but-possible. */
  lateUntil: Date | null
  /** Median first 28°F night, when dormant seeding can start. Null where it is not advised. */
  dormantFrom: Date | null
  tone: "go" | "wait" | "info"
  title: string
  detail: string
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
export const fmtOverseedDate = (d: Date) => `${MONTHS[d.getMonth()]} ${d.getDate()}`

function atNoon(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12)
}

function addDays(d: Date, days: number): Date {
  return new Date(d.getTime() + days * DAY_MS)
}

function daysFrom(a: Date, b: Date): number {
  return Math.round((atNoon(b).getTime() - atNoon(a).getTime()) / DAY_MS)
}

export interface FallOverseedWindow {
  species: OverseedSpecies
  frostDate: Date | null
  opensOn: Date
  goodUntil: Date
  lateUntil: Date
  dormantFrom: Date | null
}

/**
 * This fall's window for a species. `year` is the fall's calendar year. Null
 * frost data means a frost-free location (Sep 1 to Nov 30).
 */
export function fallOverseedWindow(
  grassType: string | null | undefined,
  frost: FrostNormals | null,
  year: number,
): FallOverseedWindow {
  const species = overseedSpeciesFor(grassType)
  const rule = OVERSEED_SPECIES[species]
  const first32 = frost?.first32Doy ?? null
  if (first32 == null) {
    const close = new Date(year, FROST_FREE_WINDOW.close.month, FROST_FREE_WINDOW.close.day, 12)
    return {
      species,
      frostDate: null,
      opensOn: new Date(year, FROST_FREE_WINDOW.open.month, FROST_FREE_WINDOW.open.day, 12),
      goodUntil: close,
      lateUntil: close,
      dormantFrom: null,
    }
  }
  const frostDate = dateFromDoy(first32, year)
  const earliest = new Date(year, EARLIEST_OPEN.month, EARLIEST_OPEN.day, 12)
  const byFrost = addDays(frostDate, -OPEN_LEAD_DAYS)
  const goodUntil = addDays(frostDate, -rule.goodLeadDays)
  const opensOn = byFrost > earliest ? byFrost : earliest
  const first28 = frost?.first28Doy ?? null
  return {
    species,
    frostDate,
    opensOn: opensOn < goodUntil ? opensOn : addDays(goodUntil, -14),
    goodUntil,
    lateUntil: addDays(frostDate, -rule.lateLeadDays),
    dormantFrom:
      first28 != null && first28 <= DORMANT_SEED_LAST_DOY
        ? addDays(dateFromDoy(first28, year), DORMANT_SEED_AFTER_HARD_FREEZE_DAYS)
        : null,
  }
}

function tooLateDetail(window: FallOverseedWindow, prefix = ""): string {
  const plan = window.dormantFrom
    ? `Dormant-seed after ${fmtOverseedDate(window.dormantFrom)}, or seed in spring.`
    : "Seed in spring."
  return `${prefix}${plan}`
}

/**
 * The fall overseeding call for a cool-season lawn on `now`. Callers decide
 * when it applies (cool-season grass, August to December); outside that span
 * the stage still reads sensibly (before the window is `waiting`).
 */
export function fallOverseedCall({ grassType, now, frost, soilF }: FallOverseedInput): FallOverseedCall {
  const today = atNoon(now)
  // Jan-Jul: this year's fall window is ahead. Aug-Dec: this fall.
  const window = fallOverseedWindow(grassType, frost ?? FALLBACK_FROST, today.getFullYear())
  const label = namedSpecies(grassType) ? OVERSEED_SPECIES[window.species].label : null
  const seedBy = (d: Date) => (label ? `Seed ${label} by ${fmtOverseedDate(d)}.` : `Seed by ${fmtOverseedDate(d)}.`)
  // A soil reading only speaks to this fall's window from August on.
  const soil = soilF != null && Number.isFinite(soilF) && today.getMonth() >= 7 ? Math.round(soilF) : null
  const knownFrost = frost?.first32Doy != null ? window.frostDate : null
  const daysToFrost = knownFrost ? daysFrom(today, knownFrost) : null
  const frostPhrase = knownFrost ? ` First frost averages ${fmtOverseedDate(knownFrost)}.` : ""
  const base = {
    species: window.species,
    daysToFrost,
    frostDate: knownFrost,
    opensOn: window.opensOn,
    goodUntil: window.goodUntil,
    lateUntil: window.lateUntil,
    dormantFrom: window.dormantFrom,
  }
  const reasonCalendar: FallOverseedReason =
    frost == null ? "no_frost_data" : window.frostDate ? "calendar" : "frost_free"

  const calendarStage: FallOverseedStage =
    today < window.opensOn
      ? "waiting"
      : today <= window.goodUntil
        ? "good"
        : today <= window.lateUntil
          ? "late"
          : "too_late"

  // Soil too cool for seed to sprout ends the season whatever the calendar says.
  if (soil != null && soil < FALL_OVERSEED_SOIL_F.min) {
    return {
      ...base,
      stage: "too_late",
      reason: "soil_cool",
      tone: "info",
      title: "Too late to overseed this fall",
      detail: tooLateDetail(window, `Soil is ${soil}°F, too cool for seed to sprout. `),
    }
  }

  if (calendarStage === "too_late") {
    return {
      ...base,
      stage: "too_late",
      reason: reasonCalendar,
      tone: "info",
      title: "Too late to overseed this fall",
      detail: tooLateDetail(window),
    }
  }

  // Warm soil only holds seed back while there is a week or more to wait.
  if (
    soil != null &&
    soil > FALL_OVERSEED_SOIL_F.max &&
    calendarStage !== "late" &&
    daysFrom(today, window.goodUntil) > SOIL_WAIT_MIN_DAYS
  ) {
    return {
      ...base,
      stage: "waiting",
      reason: "soil_warm",
      tone: "wait",
      title: `Overseed once soil drops below ${FALL_OVERSEED_SOIL_F.max}°F`,
      detail: `Soil is ${soil}°F, ${soil - FALL_OVERSEED_SOIL_F.max}° to go. ${seedBy(window.goodUntil)}`,
    }
  }

  // Before the window, August soil already in range opens it early.
  if (calendarStage === "waiting" && soil == null) {
    return {
      ...base,
      stage: "waiting",
      reason: "before_window",
      tone: "wait",
      title: `Overseeding opens ${fmtOverseedDate(window.opensOn)}`,
      detail: `${label ? `Best for ${label}` : "Best window"}: ${fmtOverseedDate(window.opensOn)} to ${fmtOverseedDate(window.goodUntil)}.`,
    }
  }

  if (calendarStage === "late") {
    return {
      ...base,
      stage: "late",
      reason: reasonCalendar,
      tone: "go",
      title: `Fall seeding window closes ${fmtOverseedDate(window.lateUntil)}`,
      detail:
        window.species === "perennial_rye"
          ? `Seed now at a heavier rate.${frostPhrase}`
          : `${label ? `Seed ${label} now.` : "Seed now."} Perennial rye fills in fastest.${frostPhrase}`,
    }
  }

  // Good by the calendar, or August soil already in range just before it opens.
  return {
    ...base,
    stage: "good",
    reason: calendarStage === "waiting" ? "calendar" : reasonCalendar,
    tone: "go",
    title: "Overseeding window is open",
    detail: `${seedBy(window.goodUntil)}${frostPhrase}`,
  }
}
